import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/molecules/Card';
import { LevelTestBanner } from '@/components/molecules/LevelTestBanner';
import { Avatar } from '@/components/atoms/Avatar';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { FormField } from '@/components/molecules/FormField';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/molecules/Dialog';
import { IconButton } from '@/components/atoms/IconButton';
import { ExpandableEditRow } from '@/components/molecules/ExpandableEditRow';
import { useProfileNavGuard } from '@/context/ProfileNavGuardContext';
import { useToast } from '@/context/ToastContext';
import { usersAPI } from '@/services/api/users.api';
import { authAPI } from '@/services/api/auth.api';
import { ApiError } from '@/services/api/client';
import { TokenService } from '@/services/auth/TokenService';
import { createChangePasswordSchema, type ChangePasswordMessages } from '@/utils/validation/auth.schemas';
import { ROUTES } from '@/constants/routes';
import { RefreshCw, X } from 'lucide-react';
import type { User } from '@/types/auth.types';
import styles from './ProfilePage.module.scss';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = Partial<
  Record<'name' | 'email' | 'currentPassword' | 'newPassword' | 'confirmPassword', string>
>;

export function ProfilePage() {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const navGuard = useProfileNavGuard();
  const { toast } = useToast();

  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [openName, setOpenName] = useState(false);
  const [openEmail, setOpenEmail] = useState(false);
  const [openPassword, setOpenPassword] = useState(false);

  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const proceedLeaveRef = useRef<(() => void) | null>(null);

  // Sync con el usuario del contexto (p. ej. tras guardar).
  useEffect(() => setName(user?.name ?? ''), [user?.name]);
  useEffect(() => setEmail(user?.email ?? ''), [user?.email]);

  const pwMessages = useMemo<ChangePasswordMessages>(
    () => ({
      currentRequired: t('profilePage.security.validation.currentRequired'),
      newMin: t('profilePage.security.validation.newMin'),
      newUpper: t('profilePage.security.validation.newUpper'),
      newLower: t('profilePage.security.validation.newLower'),
      newNumber: t('profilePage.security.validation.newNumber'),
      confirmRequired: t('profilePage.security.validation.confirmRequired'),
      mismatch: t('profilePage.security.validation.mismatch'),
      sameAsCurrent: t('profilePage.security.validation.sameAsCurrent'),
    }),
    [t]
  );

  const nameChanged = name.trim() !== (user?.name ?? '');
  const emailChanged = email.trim() !== (user?.email ?? '');
  const passwordTouched = !!(currentPassword || newPassword || confirmPassword);
  const hasChanges = nameChanged || emailChanged || passwordTouched;

  const clearError = (key: keyof FieldErrors) =>
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const handleSave = async (): Promise<boolean> => {
    // ── Validación cliente ──
    const validationErrors: FieldErrors = {};
    if (nameChanged && !name.trim()) validationErrors.name = t('profilePage.account.nameRequired');
    if (emailChanged) {
      if (!email.trim()) validationErrors.email = t('profilePage.account.emailRequired');
      else if (!EMAIL_RE.test(email.trim())) validationErrors.email = t('profilePage.account.emailInvalid');
    }
    if (passwordTouched) {
      const parsed = createChangePasswordSchema(pwMessages).safeParse({
        currentPassword,
        newPassword,
        confirmPassword,
      });
      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const field = issue.path[0] as keyof FieldErrors;
          if (field && !validationErrors[field]) validationErrors[field] = issue.message;
        }
      }
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      if (validationErrors.name) setOpenName(true);
      if (validationErrors.email) setOpenEmail(true);
      if (validationErrors.currentPassword || validationErrors.newPassword || validationErrors.confirmPassword)
        setOpenPassword(true);
      toast({ description: t('profilePage.account.errorToast') });
      return false;
    }

    // ── Guardado ──
    setSaving(true);
    const errs: FieldErrors = {};

    if (nameChanged) {
      try {
        const res = await usersAPI.updateMe({ name: name.trim() });
        updateUser({
          ...res.user,
          role: res.user.role as User['role'],
          level: res.user.level as User['level'],
        });
        setOpenName(false);
      } catch {
        errs.name = t('profilePage.account.saveError');
      }
    }

    if (emailChanged) {
      try {
        const res = await authAPI.changeEmail({ newEmail: email.trim() });
        TokenService.setToken(res.token);
        updateUser(res.user);
        setOpenEmail(false);
      } catch (e) {
        errs.email =
          e instanceof ApiError && e.status === 409
            ? t('profilePage.account.emailInUse')
            : t('profilePage.account.saveError');
      }
    }

    if (passwordTouched) {
      try {
        await authAPI.changePassword({ currentPassword, newPassword });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setOpenPassword(false);
      } catch (e) {
        errs.currentPassword =
          e instanceof ApiError && e.status === 401
            ? t('profilePage.security.currentIncorrect')
            : t('profilePage.account.saveError');
      }
    }

    setErrors(errs);
    const ok = Object.keys(errs).length === 0;
    setSaving(false);
    toast(
      ok
        ? { variant: 'success', description: t('profilePage.account.saved') }
        : { description: t('profilePage.account.errorToast') }
    );
    return ok;
  };

  // Guard de cambios sin guardar para el botón "volver" del wrapper.
  const hasChangesRef = useRef(hasChanges);
  hasChangesRef.current = hasChanges;
  useEffect(() => {
    navGuard.setGuard({
      isDirty: () => hasChangesRef.current,
      confirmLeave: (proceed) => {
        proceedLeaveRef.current = proceed;
        setLeaveOpen(true);
      },
    });
    return () => navGuard.setGuard(null);
  }, [navGuard]);

  const handleDiscardLeave = () => {
    setLeaveOpen(false);
    const proceed = proceedLeaveRef.current;
    proceedLeaveRef.current = null;
    proceed?.();
  };

  const handleSaveAndLeave = async () => {
    const ok = await handleSave();
    setLeaveOpen(false);
    if (ok) {
      const proceed = proceedLeaveRef.current;
      proceedLeaveRef.current = null;
      proceed?.();
    }
    // Si falla, se cierra la modal y se quedan los errores visibles en la página.
  };

  return (
    <div className={styles.profile}>
      <header className={styles.profile__header}>
        <h1 className={styles.profile__title}>{t('profilePage.title')}</h1>
        <p className={styles.profile__subtitle}>{t('profilePage.subtitle')}</p>
      </header>

      <LevelTestBanner />

      <Card className={styles.profile__card}>
        <CardContent>
          <div className={styles.profile__sections}>
            <section className={styles.profile__section}>
              <div className={styles.profile__avatarSection}>
                <Avatar
                  src={undefined}
                  alt={user?.name || t('common.studentFallbackName')}
                  size="xl"
                  fallback={user?.name || 'U'}
                />
                <div className={styles.profile__avatarInfo}>
                  <p className={styles.profile__avatarName}>{user?.name}</p>
                  <p className={styles.profile__avatarEmail}>{user?.email}</p>
                  {user?.level && (
                    <p className={styles.profile__avatarLevel}>
                      {t('profilePage.levelLabel', { level: user.level })}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className={styles.profile__section}>
              <h2 className={styles.profile__sectionTitle}>{t('profilePage.account.title')}</h2>
              <div className={styles.profile__fields}>
                <ExpandableEditRow
                  label={t('profilePage.account.nameLabel')}
                  value={name}
                  open={openName}
                  onToggle={() => setOpenName((o) => !o)}
                  editAriaLabel={t('profilePage.account.editName')}
                  closeAriaLabel={t('profilePage.account.cancel')}
                >
                  <Input
                    type="text"
                    value={name}
                    fullWidth
                    hasError={!!errors.name}
                    onChange={(e) => {
                      setName(e.target.value);
                      clearError('name');                    }}
                  />
                  {errors.name && <p className={styles.profile__fieldError}>{errors.name}</p>}
                </ExpandableEditRow>

                <ExpandableEditRow
                  label={t('profilePage.account.emailLabel')}
                  value={email}
                  open={openEmail}
                  onToggle={() => setOpenEmail((o) => !o)}
                  editAriaLabel={t('profilePage.account.editEmail')}
                  closeAriaLabel={t('profilePage.account.cancel')}
                >
                  <Input
                    type="email"
                    value={email}
                    fullWidth
                    autoComplete="email"
                    hasError={!!errors.email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      clearError('email');                    }}
                  />
                  {errors.email && <p className={styles.profile__fieldError}>{errors.email}</p>}
                </ExpandableEditRow>

                <ExpandableEditRow
                  label={t('profilePage.account.passwordLabel')}
                  value="••••••••"
                  open={openPassword}
                  onToggle={() => setOpenPassword((o) => !o)}
                  editAriaLabel={t('profilePage.account.editPassword')}
                  closeAriaLabel={t('profilePage.account.cancel')}
                >
                  <div className={styles.profile__passwordFields}>
                    <FormField
                      label={t('profilePage.security.currentLabel')}
                      type="password"
                      autoComplete="current-password"
                      value={currentPassword}
                      error={errors.currentPassword}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        clearError('currentPassword');                      }}
                    />
                    <FormField
                      label={t('profilePage.security.newLabel')}
                      type="password"
                      autoComplete="new-password"
                      helperText={t('profilePage.security.newHelper')}
                      value={newPassword}
                      error={errors.newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        clearError('newPassword');                      }}
                    />
                    <FormField
                      label={t('profilePage.security.confirmLabel')}
                      type="password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      error={errors.confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        clearError('confirmPassword');                      }}
                    />
                  </div>
                </ExpandableEditRow>
              </div>
            </section>
          </div>
        </CardContent>
      </Card>

      {/* Guardar: antes barra inferior fija; ahora al final del contenido (la
          barra de abajo es la navegación). */}
      <Button
        variant="primary"
        size="lg"
        fullWidth
        disabled={!hasChanges || saving}
        isLoading={saving}
        onClick={() => void handleSave()}
      >
        {t('profilePage.updateButton')}
      </Button>

      {user?.level && (
        <Button
          variant="outline"
          onClick={() => navigate(ROUTES.STUDENT.LEVEL_TEST)}
          leftIcon={<RefreshCw size={16} />}
        >
          {t('profilePage.retakeLevelTest')}
        </Button>
      )}

      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent hideDefaultClose>
          <IconButton
            icon={X}
            aria-label={t('profilePage.leaveGuard.close')}
            onClick={() => setLeaveOpen(false)}
            className={styles.profile__modalClose}
          />
          <DialogHeader>
            <DialogTitle>{t('profilePage.leaveGuard.title')}</DialogTitle>
            <DialogDescription>{t('profilePage.leaveGuard.message')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={handleDiscardLeave}>
              {t('profilePage.leaveGuard.discard')}
            </Button>
            <Button variant="primary" isLoading={saving} onClick={() => void handleSaveAndLeave()}>
              {t('profilePage.leaveGuard.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProfilePage;
